import React, { useRef, useState } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { styled, lighten, darken } from '@mui/system';
import { useGetAllSKUsQuery, useGetProductsForFamilyQuery } from '../features/products/productSlice';
import { useParams } from 'react-router-dom';
import { shortenedName } from '../helpers/strings';
import Loading from './Loading';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';

const GroupHeader = styled('div')(({ theme }) => ({
  position: 'sticky',
  top: '-8px',
  padding: '4px 10px',
  color: theme.palette.primary.main,
  backgroundColor:
    theme.palette.mode === 'light'
      ? lighten(theme.palette.primary.light, 0.85)
      : darken(theme.palette.primary.main, 0.8),
}));

const GroupItems = styled('ul')({
  padding: 0,
});

// This is for when you have all the products already loaded
const ProductAutocomplete = ({products, trimHandler = null, searchHandler = null}) => {
  const searchValue = useRef('');

  const options = products.map((sku) => {
    return {
      id: sku.id,
      code: sku.code,
      name: sku.name
    };
  });

  return (
    <Autocomplete
      id="product-search"
      options={options.sort((a, b) => -b.name.localeCompare(a.name))}
      //groupBy={(option) => option.family}
      getOptionLabel={(option) => shortenedName(option.name)}
      sx={{ width: 300, marginTop: 3 }}
      renderInput={(params) => <TextField {...params} inputRef={searchValue} label="Search products" />}
      renderGroup={(params) => (
        <li>
          <GroupHeader>{params.group}</GroupHeader>
          <GroupItems>{params.children}</GroupItems>
        </li>
      )}
      onKeyDown={(event) => {
        if (trimHandler) {
            trimHandler(searchValue.current.value)
        }
      }}
      onKeyUp={(event) => {
        if (event.key === 'Enter') {
          event.defaultMuiPrevented = true;
          // Prevent's default 'Enter' behavior.
          if(searchHandler)
              searchHandler(searchValue.current.value);
        }
      }}
    />
  );
}

const FamilyProductsAutocomplete = ({family_id = null, trimHandler = null, searchHandler = null, onChange = null}) => {
  const { data: products, error, isLoading } = useGetProductsForFamilyQuery({family_id, type: "single"}, {skip: !family_id});

  const searchValue = useRef('');
  
  if (isLoading) {
    return <Loading />;
  }
  if (error) {
    return <div>Error: {error}</div>
  }

  const options = (products?.objects ?? []).map((sku) => {
    return {
      id: sku.id,
      code: sku.code,
      name: sku.name
    };
  });

  return (
    <Autocomplete
      id="product-search"
      options={options.sort((a, b) => -b.name.localeCompare(a.name))}
      //groupBy={(option) => option.family}
      getOptionLabel={(option) => shortenedName(option.name)}
      sx={{ width: 300 }}
      renderInput={(params) => <TextField {...params} inputRef={searchValue} label="Search products" />}
      renderGroup={(params) => (
        <li>
          <GroupHeader>{params.group}</GroupHeader>
          <GroupItems>{params.children}</GroupItems>
        </li>
      )}
      onChange={(event, newValue) => {
        if (onChange) {
          onChange(newValue);
        }
      }}
      onKeyDown={(event) => {
        if (trimHandler) {
            trimHandler(searchValue.current.value)
        }
      }}
      onKeyUp={(event) => {
        if (event.key === 'Enter') {
          event.defaultMuiPrevented = true;
          // Prevent's default 'Enter' behavior.
          if(searchHandler)
              searchHandler(searchValue.current.value);
        }
      }}
    />
  );
}

// Needed for e.g. Box and material SKUs
const SingleProductTypeSelect = ({item_type = null, family_id=null, allowNull=true, selection = null, label = "Search products", onSelect = null}) => {
  const { data: products, error, isLoading } = useGetAllSKUsQuery({type: item_type, family_id: family_id});

  const searchValue = useRef('');
  const [value, setValue] = useState(selection);

  if (isLoading) {
    return <Loading />;
  }
  if (error) {
    return <div>Error: {error}</div>
  }

  let options = products.objects.map((sku) => {
    if(selection !== null && sku.id === selection.id && value === null) {
      setValue(sku.id);
    }
    return {
      id: sku.id,
      name: sku.name
    };
  });
  let nullItem = []
  if (allowNull) {
    nullItem.push(<MenuItem key={0} value={0}>None</MenuItem>);
    if(selection === null && value === null) {
      setValue(0);
    }
  }
  let product_per_id = Object.fromEntries(products.objects.map(obj => [obj.id, obj]))
  let input_id = label.toLowerCase().replace(" ","") + "_id";

  return (
    <FormControl>
    <InputLabel id={input_id}>{label}</InputLabel>
    <Select
      id="product-search"
      label={label}
      labelId={input_id}
      value={value}
      sx={{ width: 300 }}
      onChange={(event) => {
        if (onSelect) {
          setValue(event.target.value);
          if(event.target.value === 0) {
            onSelect(null);
          } else {
            onSelect(product_per_id[event.target.value]);
          }
        }
      }}
    >
      {nullItem}
      {options.map((sku) => {
        return <MenuItem key={sku.id} value={sku.id}>{sku.name}</MenuItem>
      })}
      </Select>
      </FormControl>
  );
}
export default ProductAutocomplete;
export { ProductAutocomplete, FamilyProductsAutocomplete, SingleProductTypeSelect };
